import React from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactElement<{ className?: string }>;
  variant?: "default" | "gradient" | "outlined" | "success" | "warning" | "info" | "danger";
  gradientColors?: string;
  size?: "default" | "compact";
  trend?: { value: number; direction: "up" | "down" };
  className?: string;
}

const toneMap = {
  default: "bg-slate-100 text-slate-600 ring-slate-200",
  gradient: "bg-violet-50 text-violet-700 ring-violet-100",
  outlined: "bg-slate-50 text-slate-600 ring-slate-200",
  success: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  warning: "bg-amber-50 text-amber-700 ring-amber-100",
  info: "bg-sky-50 text-sky-700 ring-sky-100",
  danger: "bg-rose-50 text-rose-700 ring-rose-100",
};

export function StatCard({
  title,
  value,
  description,
  icon,
  variant = "default",
  size = "default",
  trend,
  className,
}: StatCardProps) {
  const compact = size === "compact";

  return (
    <Card
      className={cn(
        "overflow-hidden rounded-2xl border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition-colors hover:border-slate-300",
        className,
      )}
    >
      <CardContent className={cn(compact ? "p-4" : "p-5 sm:p-6")}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{title}</p>
            <p
              className={cn(
                "mt-2 font-bold tabular-nums tracking-[-0.035em] text-slate-950",
                compact ? "text-2xl" : "text-3xl",
              )}
            >
              {value}
            </p>
          </div>
          <div
            className={cn(
              "flex shrink-0 items-center justify-center rounded-xl ring-1",
              compact ? "h-9 w-9" : "h-11 w-11",
              toneMap[variant],
            )}
          >
            {React.cloneElement(icon, { className: compact ? "h-4 w-4" : "h-5 w-5" })}
          </div>
        </div>

        {(description || trend) && (
          <div className="mt-3 flex min-h-5 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
            {trend && (
              <span
                className={cn(
                  "inline-flex items-center gap-1 font-semibold tabular-nums",
                  trend.direction === "up" ? "text-emerald-700" : "text-rose-700",
                )}
              >
                {trend.direction === "up" ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {trend.direction === "up" ? "+" : "−"}{Math.abs(trend.value)}%
              </span>
            )}
            {description && <span className="leading-5">{description}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
